import { useState, useCallback, useRef, useEffect } from "react";
import { Icons, GENDERS, classifyTraveler } from "./bookingConstants";
import { useTravelersList } from "../../api/people/usePeopleApi";
import styles from "./AddBooking.module.css";
import s from "./StepTravelers.module.css";

/* ── Traveler Row (existing traveler in the booking) ────────────────────── */

const TravelerRow = ({ traveler, index, onChange, onRemove, isDuplicate }) => {
  const type = classifyTraveler(traveler.dob);
  const typeStyles = {
    ADT: { bg: "var(--blue-50)", color: "var(--accent-blue)" },
    CHD: { bg: "var(--amber-50)", color: "var(--accent-amber)" },
    INF: { bg: "#ecfdf5", color: "#059669" },
    "—": { bg: "#f3f4f6", color: "#9ca3af" },
  };
  const ts = typeStyles[type];
  const isFromPeople = !!traveler.travellerCode;

  return (
    <div className={styles.travelerRow} style={isDuplicate ? { border: "1px solid #fca5a5", background: "#fff5f5" } : {}}>
      <div className={styles.trIndex}>{index + 1}</div>
      <div className={styles.trFields}>
        <input
          className={styles.input}
          placeholder="First name"
          value={traveler.firstName}
          onChange={(e) => onChange(index, "firstName", e.target.value)}
          readOnly={isFromPeople}
        />
        <input
          className={styles.input}
          placeholder="Last name"
          value={traveler.lastName}
          onChange={(e) => onChange(index, "lastName", e.target.value.toUpperCase())}
          style={{ textTransform: "uppercase" }}
          readOnly={isFromPeople}
        />
        <select
          className={styles.input}
          value={traveler.gender || ""}
          onChange={(e) => onChange(index, "gender", e.target.value)}
          disabled={isFromPeople}
        >
          <option value="">Gender...</option>
          {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <input
          className={styles.input}
          type="date"
          value={traveler.dob}
          onChange={(e) => onChange(index, "dob", e.target.value)}
          readOnly={isFromPeople}
        />
        <span className={styles.trType} style={{ background: ts.bg, color: ts.color }}>{type}</span>
        {isDuplicate && (
          <span style={{ fontSize: 11, fontWeight: 600, color: "#b91c1c", background: "#fee2e2", padding: "2px 7px", borderRadius: 4, whiteSpace: "nowrap" }}>
            ⚠ Duplicate
          </span>
        )}
      </div>
      <button className={styles.trRemove} onClick={() => onRemove(index)} title="Remove traveler">{Icons.trash}</button>
    </div>
  );
};

/* ── Format gender from API (MALE → Male) ──────────────────────────────── */

const fmtGender = (g) => {
  if (!g) return "";
  return g.charAt(0).toUpperCase() + g.slice(1).toLowerCase();
};

const fmtDate = (d) => {
  if (!d) return "";
  const iso = d.slice(0, 10);
  const parts = iso.split("-");
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return iso;
};

/* ── People Search Panel ────────────────────────────────────────────────── */

const PeopleSearchPanel = ({ onSelect, onClose, existingCodes }) => {
  const [query, setQuery] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleQueryChange = useCallback((val) => {
    setQuery(val);
    setPage(1);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQ(val), 300);
  }, []);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const { data: travelers, pagination, loading } = useTravelersList({
    q: debouncedQ,
    page,
    limit: 8,
    status: "Active",
  });

  const totalPages = pagination?.totalPages || 1;

  const handleSelect = (person) => {
    onSelect({
      firstName: person.firstName || "",
      lastName: (person.lastName || "").toUpperCase(),
      gender: fmtGender(person.gender),
      dob: person.dateOfBirth ? person.dateOfBirth.slice(0, 10) : "",
      travellerCode: person.travellerCode || null,
      nationality: person.nationality || "",
    });
  };

  const typeStyles = {
    ADT: { bg: "#eef2ff", color: "#4338ca" },
    CHD: { bg: "#fef3e2", color: "#b45309" },
    INF: { bg: "#ecfdf5", color: "#047857" },
  };

  return (
    <div className={s.searchPanel}>
      <div className={s.searchHeader}>
        <div className={s.searchTitle}>{Icons.users} Select Traveler from People</div>
        <button className={s.searchClose} onClick={onClose}>{Icons.x}</button>
      </div>

      <div className={s.searchBar}>
        <span className={s.searchIcon}>{Icons.search}</span>
        <input
          ref={inputRef}
          className={s.searchInput}
          placeholder="Search by name, code, or nationality..."
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
        />
        {query && (
          <button className={s.searchClear} onClick={() => handleQueryChange("")}>{Icons.x}</button>
        )}
      </div>

      <div className={s.searchResults}>
        {loading && travelers.length === 0 && (
          <div className={s.searchLoading}>Loading travelers...</div>
        )}

        {!loading && travelers.length === 0 && (
          <div className={s.searchEmpty}>
            <span className={s.searchEmptyIcon}>{Icons.users}</span>
            <span>{debouncedQ ? "No travelers found" : "Start typing to search"}</span>
          </div>
        )}

        {travelers.map((person) => {
          const ageType = person.ageType || classifyTraveler(person.dateOfBirth);
          const ts = typeStyles[ageType] || { bg: "#f3f4f6", color: "#9ca3af" };
          const alreadyAdded = existingCodes.includes(person.travellerCode);

          return (
            <button
              key={person.travellerCode || person.id}
              className={`${s.personRow} ${alreadyAdded ? s.personRowAdded : ""}`}
              onClick={() => !alreadyAdded && handleSelect(person)}
              disabled={alreadyAdded}
            >
              <div className={s.personAvatar}>
                {(person.firstName || "?")[0]}{(person.lastName || "?")[0]}
              </div>
              <div className={s.personInfo}>
                <div className={s.personName}>
                  {person.firstName} <strong>{(person.lastName || "").toUpperCase()}</strong>
                </div>
                <div className={s.personMeta}>
                  <span className={s.personCode}>{person.travellerCode}</span>
                  {person.nationality && <><span className={s.personDot}>·</span><span>{person.nationality}</span></>}
                  {person.dateOfBirth && <><span className={s.personDot}>·</span><span>{fmtDate(person.dateOfBirth)}</span></>}
                </div>
              </div>
              <span className={s.personType} style={{ background: ts.bg, color: ts.color }}>{ageType}</span>
              {alreadyAdded ? (
                <span className={s.personAdded}>{Icons.check} Added</span>
              ) : (
                <span className={s.personSelect}>{Icons.plus}</span>
              )}
            </button>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className={s.searchPagination}>
          <button
            className={s.pageBtn}
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <span style={{ transform: "rotate(180deg)", display: "flex" }}>{Icons.chevronRight}</span>
          </button>
          <span className={s.pageInfo}>
            Page {page} of {totalPages}
            {pagination?.total != null && <span className={s.pageTotal}> · {pagination.total} travelers</span>}
          </span>
          <button
            className={s.pageBtn}
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            {Icons.chevronRight}
          </button>
        </div>
      )}
    </div>
  );
};

/* ── Duplicate detection helper ─────────────────────────────────────────── */

const isSameTraveler = (a, b) =>
  a.firstName?.trim().toLowerCase() === b.firstName?.trim().toLowerCase() &&
  a.lastName?.trim().toLowerCase()  === b.lastName?.trim().toLowerCase()  &&
  (a.gender  || "").toLowerCase()   === (b.gender  || "").toLowerCase()   &&
  (a.dob     || "")                 === (b.dob     || "");

const findDuplicateIndex = (travelers, candidate, excludeIndex = -1) =>
  travelers.findIndex((t, i) => i !== excludeIndex && isSameTraveler(t, candidate));

/* ── Main Component ─────────────────────────────────────────────────────── */

const StepTravelers = ({ data, onChange, onRemoveTraveler }) => {
  const [showSearch, setShowSearch] = useState(false);
  const [dupWarning, setDupWarning] = useState(null);

  const addBlankTraveler = () => {
    onChange("travelers", [...data.travelers, { firstName: "", lastName: "", dob: "", gender: "" }]);
  };

  const addFromPeople = (person) => {
    // Block if already in list by travellerCode
    if (person.travellerCode && data.travelers.some((t) => t.travellerCode === person.travellerCode)) {
      setDupWarning(`${person.firstName} ${person.lastName} is already in the traveler list.`);
      return;
    }
    // Block if same name + gender + DOB already in list
    if (findDuplicateIndex(data.travelers, person) !== -1) {
      setDupWarning(
        `A traveler with the same name, gender, and date of birth (${person.firstName} ${person.lastName}) is already in the list.`
      );
      return;
    }
    setDupWarning(null);
    onChange("travelers", [...data.travelers, person]);
  };

  const updateTraveler = (index, field, value) => {
    const next = [...data.travelers];
    next[index] = { ...next[index], [field]: value };
    onChange("travelers", next);
  };

  const removeTraveler = (index) => {
    if (onRemoveTraveler) { onRemoveTraveler(data.travelers[index], index); return; }
    onChange("travelers", data.travelers.filter((_, i) => i !== index));
    onChange("assignments", (data.assignments || []).filter((a) => a.travelerIndex !== index).map((a) => ({
      ...a,
      travelerIndex: a.travelerIndex > index ? a.travelerIndex - 1 : a.travelerIndex,
    })));
  };

  const existingCodes = data.travelers.filter((t) => t.travellerCode).map((t) => t.travellerCode);

  // Build a set of indexes that are duplicates of an earlier row
  const duplicateIndexes = new Set(
    data.travelers.reduce((acc, t, i) => {
      if (t.firstName && t.lastName && t.dob) {
        const earlierMatch = findDuplicateIndex(data.travelers, t, i);
        if (earlierMatch !== -1 && earlierMatch < i) acc.push(i);
      }
      return acc;
    }, [])
  );

  return (
    <div className={styles.stepContent}>
      <div className={styles.sectionBlock}>
        <div className={styles.sectionLabelRow}>
          <div className={styles.sectionLabel}>Travelers ({data.travelers.length})</div>
          <button className={s.selectBtn} onClick={() => setShowSearch(!showSearch)}>
            {Icons.search} Select from Travelers List
          </button>
        </div>

        {dupWarning && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: "10px 14px", marginBottom: 10, color: "#b91c1c", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
            <span>⚠️</span>
            <span>{dupWarning}</span>
            <button onClick={() => setDupWarning(null)} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#b91c1c", fontWeight: 700 }}>✕</button>
          </div>
        )}

        {showSearch && (
          <PeopleSearchPanel
            onSelect={addFromPeople}
            onClose={() => setShowSearch(false)}
            existingCodes={existingCodes}
          />
        )}

        {data.travelers.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIconWrap}>{Icons.users}</div>
            <div className={styles.emptyTitle}>No travelers added yet</div>
            <div className={styles.emptyDesc}>
              Select existing travelers from your People database or add new ones manually.
              Age classification (ADT/CHD/INF) is calculated automatically from date of birth.
            </div>
            <div className={s.emptyActions}>
              <button className={s.selectBtn} onClick={() => setShowSearch(true)}>
                {Icons.search} Select from People
              </button>
              <button className={styles.addBtn} onClick={addBlankTraveler}>
                {Icons.plus} Add Manually
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.travelerHeader}>
              <div className={styles.trHdrIndex}>#</div>
              <div className={styles.trHdrFields}>
                <span>First Name</span><span>Last Name</span><span>Gender</span><span>Date of Birth</span><span>Type</span>
              </div>
              <div className={styles.trHdrAction} />
            </div>
            <div className={styles.travelerList}>
              {data.travelers.map((t, i) => (
                <TravelerRow key={i} traveler={t} index={i} onChange={updateTraveler} onRemove={removeTraveler} isDuplicate={duplicateIndexes.has(i)} />
              ))}
            </div>
            <button className={s.addNewBtn} onClick={addBlankTraveler}>
              {Icons.plus} Add New Traveler
            </button>
          </>
        )}
      </div>

      <div className={styles.infoBox}>
        <strong>Auto-classification:</strong> ADT = 12+ years · CHD = 2–11 years · INF = 0–1 years.
        Classification is derived from the date of birth.
      </div>
    </div>
  );
};

export default StepTravelers;
